import { LinkedinIcon } from '@assets/icons/linkedin';
import NewFileUploader from '@components/NewFileUploader';
import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import CustomTooltip from '@components/tooltip/tooltip';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import { FormControlLabel, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary } from '@utils/util.types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrops, useCropsStore } from 'services/crops';
import { useUploadStore } from 'services/file-manager';
import { useLanguages, useLanguagesStore } from 'services/languages';
import { useDeleteUserFile, useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';
import { ALLOWED_FILE_FORMATS } from '../specialist/SpecialistStepResume';
import '../styles.css';

const initialValues = {
	resume: '',
	socialLinks: '',
	userLanguages: [],
	crops: [],
	user_agreed_term_and_condition: false,
	userFiles: [],
};

interface PersonalDetailsFormValues {
	userLanguages: Dictionary[];
	crops: Dictionary[];
	user_agreed_term_and_condition: boolean;
	resume: string;
	socialLinks: string;
	userFiles: { file: string }[];
}

const personalDetailsValidationSchema = Yup.object().shape({
	userLanguages: Yup.array().min(1, 'fieldNameError'),
	crops: Yup.array().min(1, 'fieldNameError'),
	user_agreed_term_and_condition: Yup.bool().oneOf([true], 'userAgreementError'),
	resume: Yup.string().required('uploadPresentationError'),
	socialLinks: Yup.string()
		.test('is-linkedin', 'mustBeLinkedInUrl', (value) => {
			if (!value) return true; // Allow empty value

			return value.includes('linkedin.com');
		})
		.url('invalidUrl'),
});

export function CompanyRegistrationResumeStep() {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { crops } = useCropsStore();
	const { languages } = useLanguagesStore();
	const mutate = useUpdateUser();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { uploadStatus } = useUploadStore();

	const { user } = useUsersStore();
	const removeFile = useDeleteUserFile();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const [existingFiles, setExistingFiles] = useState<
		Array<{
			id: string;
			fileType: string;
			s3_key: string;
			filename: string;
		}>
	>([]);

	useCrops();
	useLanguages();

	useEffect(() => {
		if (user?.user_files && user.user_files.length > 0) {
			const formattedFiles = user.user_files.map((file) => ({
				id: file.id,
				fileType: 'document',
				s3_key: file.s3_key,
				filename: file.filename || file.s3_key.split('/').pop() || 'Resume',
			}));

			setExistingFiles(formattedFiles);

			if (formattedFiles.length > 0) {
				formik.setFieldValue('resume', formattedFiles[0].filename);
				formik.setFieldValue('userFiles', [{ file: formattedFiles[0].s3_key }]);
			}
		}
	}, [user?.user_files]);

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);
	const languagesDictionary = useMemo(() => mapToDictionary(languages), [languages]);

	const formik = useFormik<PersonalDetailsFormValues>({
		initialValues: initialValues,
		validationSchema: personalDetailsValidationSchema,
		onSubmit: async (values) => {
			try {
				setIsSubmitting(true);
				const formattedValue = {
					...values,
					userLanguages: values.userLanguages.map((language) => language.id),
					crops: values.crops.map((crop) => crop.id),
					socialLinks: [{ linkedin: values.socialLinks }],
					userFiles: values.userFiles,
					user_completed_registration: true,
				};

				await mutate(formattedValue);
				navigateWithLanguage('/specialist-switcher');
			} catch (error) {
				console.error(error);
				showSnackbar(t('error'), t('failedToSubmit'), 'error');
			} finally {
				setIsSubmitting(false);
			}
		},
	});

	const renderAutocomplete = (
		label: string,
		name: keyof PersonalDetailsFormValues,
		options: Dictionary[]
	) => {
		return (
			<div>
				<TaAutocomplete
					required
					filterSelectedOptions
					options={options}
					getOptionLabel={(option) => option.name}
					label={t(label)}
					icon={
						name === 'crops' ? (
							<SpaOutlinedIcon
								color={formik.touched[name] && formik.errors[name] ? 'error' : 'primary'}
							/>
						) : (
							<RecordVoiceOverOutlinedIcon
								color={formik.touched[name] && formik.errors[name] ? 'error' : 'primary'}
							/>
						)
					}
					multiple
					value={formik.values[name] as Dictionary[]}
					onChange={(event, value) => formik.setFieldValue(name, value)}
					error={Boolean(formik.touched[name] && formik.errors[name])}
				/>
				{formik.touched[name] && formik.errors[name] && (
					<ErrorMessage message={t(formik.errors[name] as string, { fieldName: t(label) })} />
				)}
			</div>
		);
	};

	const handleFileRemove = async (fileId: string) => {
		try {
			await removeFile(fileId);
			formik.setFieldValue('resume', '');
			formik.setFieldValue('userFiles', []);
			formik.setFieldTouched('resume', true);
			showSnackbar(t('fileRemoved'), t('fileRemovedSuccessfully'), 'success');
		} catch (error) {
			showSnackbar(t('error'), t('failedToRemoveFile'), 'error');
		}
	};

	const handleNewFileUploaderRemove = useCallback(
		(index: number, file: any) => {
			if (file && file.key) {
				user?.user_files?.forEach(async (userFile: any) => {
					if (userFile.s3_key === file.key) {
						await removeFile(userFile.id);
					}
				});
			}

			if (existingFiles.length === 0) {
				formik.setFieldValue('resume', '');
				formik.setFieldValue('userFiles', []);
			}

			showSnackbar(t('fileRemoved'), t('fileRemovedSuccessfully'), 'success');
		},
		[existingFiles, formik, removeFile, showSnackbar, user?.user_files]
	);

	const handleExistingFileRemove = async (id: string) => {
		try {
			await removeFile(id);

			setExistingFiles((prev) => prev.filter((file) => file.id !== id));

			if (existingFiles.length <= 1) {
				formik.setFieldValue('resume', '');
				formik.setFieldValue('userFiles', []);
			}

			showSnackbar(t('fileRemoved'), t('fileRemovedSuccessfully'), 'success');
		} catch (error) {
			showSnackbar(t('error'), t('failedToRemoveFile'), 'error');
		}
	};

	const handleOnUpload = useCallback(
		async (status: any) => {
			if (status && status.key) {
				try {
					formik.setFieldValue('resume', status.fileName);
					formik.setFieldValue('userFiles', [{ file: status.key }]);
					formik.setFieldTouched('resume', true);

					await mutate({
						userFiles: [{ file: status.key }],
					});

					showSnackbar('File uploaded', 'File uploaded successfully', 'success');
				} catch (error) {
					showSnackbar('Error', 'Failed to upload file', 'error');
				}
			}
		},
		[formik, showSnackbar, mutate]
	);

	return (
		<>
			<form onSubmit={formik.handleSubmit} className="flex flex-col gap-[20px] pb-5">
				<p className="text-base text-dark font-bold uppercase">{t('resume')}</p>
				<div>
					<CustomTooltip
						marginLeftArrow={true}
						placement="top"
						title={
							<Box className="flex items-center gap-[10px] text-blue">
								<ErrorOutlineOutlinedIcon />
								<Typography>{t('cvUploadTooltipText')}</Typography>
							</Box>
						}
					>
						<div className="w-full">
							<NewFileUploader
								onUploaded={handleOnUpload}
								title={t('uploadPresentation')}
								description={t('cvUploadTooltipText')}
								allowedFormats={ALLOWED_FILE_FORMATS}
								onFileRemove={handleNewFileUploaderRemove}
								onExistingFileRemove={handleExistingFileRemove}
								existingFiles={existingFiles}
								multiple={false}
								showSupportedFormats={true}
								maxFileSize={50 * 1024 * 1024}
								showRemoveButton
							/>
							{formik.touched.resume && formik.errors.resume && (
								<ErrorMessage message={t(formik.errors.resume || '')} />
							)}
						</div>
					</CustomTooltip>
				</div>

				<div>
					<TaInput
						icon={<LinkedinIcon />}
						id={'socialLinks'}
						label={t('socialProfileUrl')}
						size={'small'}
						value={formik.values.socialLinks}
						onChange={formik.handleChange}
						error={formik.errors.socialLinks}
						touched={formik.touched.socialLinks}
					/>
					{formik.touched.socialLinks && formik.errors.socialLinks && (
						<ErrorMessage message={t(formik.errors.socialLinks || '')} />
					)}
				</div>

				<p className="text-base text-dark font-bold">{t('preferences')}</p>
				{renderAutocomplete('communicationLanguage', 'userLanguages', languagesDictionary)}
				{renderAutocomplete('selectCrops', 'crops', cropsDictionary)}
				<div>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.user_agreed_term_and_condition}
								onChange={formik.handleChange}
								name="user_agreed_term_and_condition"
								sx={{
									paddingTop: 0,

									'& .MuiSvgIcon-root': {
										color:
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition
												? '#FD3333'
												: '#00AB89',
									},
								}}
							/>
						}
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
						}}
						label={
							<span
								className={classNames('agreement-grey-text', {
									'!text-error':
										formik.touched.user_agreed_term_and_condition &&
										formik.errors.user_agreed_term_and_condition,
								})}
							>
								{t('agree')}{' '}
								<a
									href="https://telagri.com/terms-conditions"
									target="_blank"
									className={classNames('agreement-green-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('termsAndConditions')}
								</a>{' '}
								<a
									href="https://telagri.com/privacy-policy"
									target="_blank"
									className={classNames('agreement-green-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('privacyPolicy')}
								</a>{' '}
								<span
									className={classNames('agreement-grey-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('and')}
								</span>{' '}
								<a
									href="https://telagri.com/specific-terms"
									target="_blank"
									className={classNames('agreement-green-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('specificTerms')}
								</a>
							</span>
						}
					/>
					{formik.touched.user_agreed_term_and_condition &&
						formik.errors.user_agreed_term_and_condition && (
							<ErrorMessage
								message={t(formik.errors.user_agreed_term_and_condition || '', {
									fieldName: t('user_agreed_term_and_condition'),
								})}
							/>
						)}
				</div>

				<Button color="primary" variant="contained" fullWidth type="submit" loading={isSubmitting}>
					{t('singUp')}
				</Button>
			</form>
		</>
	);
}
